'use client';

import { Suspense, useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { CheckCircle2 } from 'lucide-react';
import { API_ROUTES, buildApiUrl } from '@/config/api-routes';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  DynamicFormRenderer,
  validateFormFields,
} from '@/components/form-templates/dynamic-form-renderer';
import type { PublicAssessmentForm } from '@/types/assessment-form';

type Status = 'loading' | 'ready' | 'error' | 'submitted';

function draftKey(token: string) {
  return `form-draft:${token}`;
}

function readDraft(token: string): Record<string, unknown> {
  try {
    const raw = localStorage.getItem(draftKey(token));
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeDraft(token: string, values: Record<string, unknown>) {
  try {
    localStorage.setItem(draftKey(token), JSON.stringify(values));
  } catch {
    return;
  }
}

function clearDraft(token: string) {
  try {
    localStorage.removeItem(draftKey(token));
  } catch {
    return;
  }
}

async function readError(res: Response, fallback: string): Promise<string> {
  try {
    const body = await res.json();
    const message = Array.isArray(body?.message)
      ? body.message[0]
      : body?.message;
    return typeof message === 'string' && message ? message : fallback;
  } catch {
    return fallback;
  }
}

function PublicFormContent() {
  const token = useSearchParams().get('t') ?? '';
  const [status, setStatus] = useState<Status>('loading');
  const [form, setForm] = useState<PublicAssessmentForm | null>(null);
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState('');
  const [website, setWebsite] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, []);

  const load = useCallback(async () => {
    if (!token) {
      setMessage('This form link is not valid');
      setStatus('error');
      return;
    }
    setStatus('loading');
    try {
      const res = await fetch(
        buildApiUrl(API_ROUTES.ASSESSMENT_FORMS.PUBLIC_GET(token)),
      );
      if (!res.ok) {
        setMessage(await readError(res, 'This form link is not valid'));
        setStatus('error');
        return;
      }
      const body = await res.json();
      const data: PublicAssessmentForm = body.data;
      setForm(data);
      setValues({
        ...data.prefill,
        ...readDraft(token),
        ...pick(data.prefill, data.lockedFieldIds),
      });
      document.title = `${data.name} | ${data.organization.name}`;
      setStatus('ready');
    } catch {
      setMessage(
        'Could not load the form. Check your connection and try again.',
      );
      setStatus('error');
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const handleChange = (fieldId: string, value: unknown) => {
    setValues((prev) => {
      const next = { ...prev, [fieldId]: value };
      writeDraft(token, next);
      return next;
    });
    if (errors[fieldId]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[fieldId];
        return next;
      });
    }
  };

  const handleSubmit = async () => {
    if (!form) return;
    const validation = validateFormFields(form.fields, values);
    setErrors(validation);
    const firstError = Object.keys(validation)[0];
    if (firstError) {
      document
        .querySelector(`[data-field-id="${firstError}"]`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setMessage('Please answer the required questions');
      return;
    }
    setIsSubmitting(true);
    setMessage('');
    try {
      const res = await fetch(
        buildApiUrl(API_ROUTES.ASSESSMENT_FORMS.PUBLIC_SUBMIT(token)),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ answers: values, website }),
        },
      );
      if (!res.ok) {
        setMessage(await readError(res, 'Could not submit. Please try again.'));
        return;
      }
      clearDraft(token);
      setStatus('submitted');
      window.scrollTo({ top: 0 });
    } catch {
      setMessage('Could not submit. Check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (status === 'loading') {
    return (
      <div className="space-y-4">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="rounded-lg border bg-white dark:bg-dark-card p-8 text-center">
        <p className="font-medium">{message}</p>
        <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
          Please contact your dietitian for a new link.
        </p>
      </div>
    );
  }

  if (!form) return null;

  if (status === 'submitted') {
    return (
      <div className="rounded-lg border bg-white dark:bg-dark-card p-8 text-center">
        <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-600 dark:text-emerald-400" />
        <h2 className="mt-3 text-lg font-semibold">Thank you!</h2>
        <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
          Your answers were sent to {form.organization.name}.
        </p>
        {form.isPersonal && form.isReusable && (
          <Button className="mt-4" variant="outline" onClick={load}>
            Submit another response
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="rounded-lg border-t-4 border-t-zinc-900 bg-white dark:border-t-zinc-100 dark:bg-dark-card p-6 shadow-sm">
        <div className="flex items-center gap-3">
          {form.organization.logo && (
            <img
              src={form.organization.logo}
              alt=""
              className="h-10 w-10 rounded object-contain"
            />
          )}
          <span className="text-sm font-medium text-zinc-500 dark:text-zinc-400">
            {form.organization.name}
          </span>
        </div>
        <h1 className="mt-3 text-2xl font-semibold tracking-tight">
          {form.name}
        </h1>
        {form.description && (
          <p className="mt-2 whitespace-pre-wrap text-sm text-zinc-500 dark:text-zinc-400">
            {form.description}
          </p>
        )}
        <p className="mt-3 text-xs text-zinc-500 dark:text-zinc-400">
          <span className="text-red-500">*</span> Required
        </p>
      </div>

      <DynamicFormRenderer
        fields={form.fields}
        sections={form.sections}
        values={values}
        onChange={handleChange}
        errors={errors}
        lockedFieldIds={form.lockedFieldIds}
        disabled={isSubmitting}
      />

      <input
        type="text"
        name="website"
        value={website}
        onChange={(e) => setWebsite(e.target.value)}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        className="absolute left-[-9999px] h-0 w-0 opacity-0"
      />

      <div className="space-y-2 pb-10">
        {message && <p className="text-sm text-destructive">{message}</p>}
        <Button
          size="lg"
          className="w-full sm:w-auto"
          onClick={handleSubmit}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Submitting...' : 'Submit'}
        </Button>
      </div>
    </div>
  );
}

function pick(source: Record<string, unknown>, keys: string[]) {
  return Object.fromEntries(keys.map((k) => [k, source[k]]));
}

export default function PublicFormPage() {
  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-zinc-900">
      <div className="mx-auto w-full max-w-2xl px-4 py-8">
        <Suspense fallback={<Skeleton className="h-64 w-full" />}>
          <PublicFormContent />
        </Suspense>
      </div>
    </div>
  );
}
